import { useEffect, useMemo, useState } from "react";
import { hasRole } from "@/lib/roles";
import api, { formatApiError } from "@/lib/api";
import { employeeMatches } from "@/lib/employeeSearch";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Check, Ban, Trash2, Search, Send, Award, Edit3, CalendarRange } from "lucide-react";
import PerformanceCycleDialog from "@/components/PerformanceCycleDialog";

function fmtScore(n) {
    if (n == null) return "0";
    return Number(n).toFixed(1);
}

const STATUS_PILLS = {
    draft: { label: "Draft", cls: "bg-ink/5 text-ink/70 border-ink/10" },
    submitted: { label: "Pending Dept", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    dept_approved: { label: "Pending HR", cls: "bg-purple-50 text-purple-700 border-purple-200" },
    finalized: { label: "Finalized", cls: "bg-green-50 text-green-700 border-green-200" },
    rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
};

function StatusPill({ status }) {
    const s = STATUS_PILLS[status] || { label: status, cls: "bg-ink/5 text-ink/70 border-ink/10" };
    return (
        <span className={`inline-flex items-center px-2 py-1 border text-[10px] uppercase tracking-[0.15em] ${s.cls}`}>
            {s.label}
        </span>
    );
}

function ratingColor(rating) {
    if (rating === "Outstanding") return "text-green-700";
    if (rating === "Exceeds expectations") return "text-klein";
    if (rating === "Meets expectations") return "text-ink/70";
    return "text-destructive";
}

export default function Performance() {
    const { user } = useAuth();
    const role = user?.role || "";
    const isFinanceLead =
        hasRole(user, "department_lead") && (user?.department || "").trim().toLowerCase() === "finance";

    const canCreate = hasRole(user, "admin", "hr", "appraiser") || (hasRole(user, "department_lead") && !isFinanceLead);
    const canHrApprove = hasRole(user, "admin", "hr");

    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [criteria, setCriteria] = useState([]);
    const [maxScore, setMaxScore] = useState(10);
    const [statusFilter, setStatusFilter] = useState("all");
    const [periodFilter, setPeriodFilter] = useState("");
    const [loading, setLoading] = useState(true);

    const [open, setOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ employee_id: "", period: "", scores: {}, manager_comments: "" });
    const [empSearch, setEmpSearch] = useState("");
    const [editingId, setEditingId] = useState(null);

    // Annual cycle dialog
    const [cycleOpen, setCycleOpen] = useState(false);

    const canLaunchCycle = hasRole(user, "admin", "hr");

    const load = async () => {
        setLoading(true);
        try {
            const [list, emps, cs] = await Promise.all([
                api.get("/performance", { params: { status: statusFilter !== "all" ? statusFilter : undefined, period: periodFilter || undefined } }),
                api.get("/employees"),
                api.get("/company-settings"),
            ]);
            setItems(list.data);
            setEmployees(emps.data);
            setCriteria(cs.data?.performance_criteria || []);
            setMaxScore(cs.data?.performance_max_score || 10);
        } catch (err) {
            toast.error("Failed to load reviews");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, periodFilter]);

    const filteredEmployees = useMemo(() => {
        const q = empSearch.trim().toLowerCase();
        if (!q) return employees.slice(0, 30);
        return employees.filter((e) => employeeMatches(e, q)).slice(0, 30);
    }, [empSearch, employees]);

    const selectedEmp = useMemo(
        () => employees.find((e) => e.id === form.employee_id),
        [form.employee_id, employees],
    );

    const openCreate = () => {
        const blank = Object.fromEntries(criteria.map((c) => [c.key, 0]));
        setForm({ employee_id: "", period: "", scores: blank, manager_comments: "" });
        setEditingId(null);
        setEmpSearch("");
        setOpen(true);
    };

    const openEdit = (r) => {
        setForm({
            employee_id: r.employee_id,
            period: r.period,
            scores: { ...(r.scores || {}) },
            manager_comments: r.manager_comments || "",
        });
        setEditingId(r.id);
        setOpen(true);
    };

    const setScore = (key, value) => {
        const v = Math.max(0, Math.min(maxScore, Number(value) || 0));
        setForm((f) => ({ ...f, scores: { ...f.scores, [key]: v } }));
    };

    const previewTotals = useMemo(() => {
        if (!criteria.length) return { weighted: 0, normalized: 0, rating: "—" };
        let weighted = 0;
        criteria.forEach((c) => {
            const s = Math.max(0, Math.min(maxScore, Number(form.scores?.[c.key] || 0)));
            weighted += s * (Number(c.weight || 0) / 100);
        });
        const normalized = (weighted / maxScore) * 100;
        const rating =
            normalized >= 85 ? "Outstanding" :
            normalized >= 70 ? "Exceeds expectations" :
            normalized >= 50 ? "Meets expectations" : "Below expectations";
        return { weighted: weighted.toFixed(2), normalized: normalized.toFixed(1), rating };
    }, [form.scores, criteria, maxScore]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.employee_id) { toast.error("Pick an employee"); return; }
        if (!form.period.trim()) { toast.error("Period is required"); return; }
        setCreating(true);
        try {
            if (editingId) {
                await api.put(`/performance/${editingId}`, { scores: form.scores, manager_comments: form.manager_comments });
                toast.success("Review updated");
            } else {
                await api.post(`/performance`, form);
                toast.success("Review created (draft)");
            }
            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setCreating(false);
        }
    };

    const [commentDialog, setCommentDialog] = useState(null); // { review, kind, note }

    const action = async (r, kind) => {
        let body = {};
        if (kind === "reject") {
            const reason = window.prompt("Reason for rejection?");
            if (!reason) return;
            body = { reason };
        } else if (kind === "submit") {
            if (!window.confirm("Submit this review for Dept Lead approval?")) return;
        } else {
            // dept-approve / hr-approve → open dedicated comment dialog
            setCommentDialog({ review: r, kind, note: "" });
            return;
        }
        try {
            await api.put(`/performance/${r.id}/${kind}`, body);
            toast.success(`Review ${kind.replace("-", " ")}`);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
        }
    };

    const submitCommentDialog = async () => {
        if (!commentDialog) return;
        const { review, kind, note } = commentDialog;
        if (kind === "dept-approve" && !(note || "").trim()) {
            toast.error("Please leave a short comment before approving — the employee will see this feedback.");
            return;
        }
        try {
            await api.put(`/performance/${review.id}/${kind}`, { note: (note || "").trim() });
            toast.success(`Review ${kind.replace("-", " ")}`);
            setCommentDialog(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
        }
    };

    const onDelete = async (r) => {
        if (!window.confirm(`Delete review for ${r.employee_name}?`)) return;
        try {
            await api.delete(`/performance/${r.id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
        }
    };

    return (
        <div className="px-8 md:px-12 py-12 md:py-16" data-testid="performance-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">Talent management</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        Performance evaluation
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-xl">
                        Score employees on configured criteria, then route through Dept Lead and HR for final approval.
                    </p>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2 flex-wrap">
                        {canLaunchCycle && (
                            <button
                                onClick={() => setCycleOpen(true)}
                                className="inline-flex items-center gap-2 border border-klein text-klein px-5 py-3 text-sm hover:bg-klein hover:text-white transition-colors"
                                data-testid="launch-cycle-button"
                                title="Bulk-create draft reviews for every active employee"
                            >
                                <CalendarRange className="w-4 h-4" /> Launch annual cycle
                            </button>
                        )}
                        <button
                            onClick={openCreate}
                            className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90"
                            data-testid="add-perf-button"
                        >
                            <Plus className="w-4 h-4" /> New review
                        </button>
                    </div>
                )}
            </div>

            <div className="mt-8 flex items-center gap-2 flex-wrap">
                {["all", "draft", "submitted", "dept_approved", "finalized", "rejected"].map((s) => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] border ${
                            statusFilter === s ? "bg-ink text-white border-ink" : "border-ink/15 text-ink/70 hover:border-ink/40"
                        }`}
                        data-testid={`perf-filter-${s}`}
                    >
                        {STATUS_PILLS[s]?.label || "All"}
                    </button>
                ))}
                <input
                    type="text"
                    placeholder="Filter by period (e.g. 2026-H1)"
                    value={periodFilter}
                    onChange={(e) => setPeriodFilter(e.target.value)}
                    className="ml-auto border border-ink/15 px-3 py-1.5 text-sm focus:border-klein focus:outline-none"
                    data-testid="perf-period-filter"
                />
            </div>

            <div className="mt-6 bg-white border border-ink/10 overflow-auto max-h-[520px]">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b-2 border-ink/20 bg-bone">
                            <th className="text-right overline px-4 py-3 w-[60px]">Rank</th>
                            <th className="text-left overline px-4 py-3">Employee</th>
                            <th className="text-left overline px-4 py-3 hidden md:table-cell">Period</th>
                            <th className="text-right overline px-4 py-3">Score</th>
                            <th className="text-left overline px-4 py-3 hidden md:table-cell">Rating</th>
                            <th className="text-left overline px-4 py-3">Stage</th>
                            <th className="text-right overline px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" className="px-6 py-16 text-center text-ink/40">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan="7" className="px-6 py-16 text-center text-ink/40">No reviews yet.</td></tr>
                        ) : items.map((r, i) => (
                            <tr key={r.id} className="border-b border-ink/10 hover:bg-bone/40" data-testid={`perf-row-${r.id}`}>
                                <td className="px-4 py-3 text-right text-ink/40 font-display font-bold tabular-nums">{i + 1}</td>
                                <td className="px-4 py-3">
                                    <div className="text-ink font-medium">{r.employee_name}</div>
                                    <div className="text-xs text-ink/50">{r.department || "—"}</div>
                                </td>
                                <td className="px-4 py-3 hidden md:table-cell">{r.period}</td>
                                <td className="px-4 py-3 text-right tabular-nums">
                                    <span className="font-display font-bold text-lg text-klein">{fmtScore(r.normalized_score)}</span>
                                    <span className="text-xs text-ink/40">/100</span>
                                </td>
                                <td className={`px-4 py-3 hidden md:table-cell font-medium ${ratingColor(r.rating)}`}>{r.rating}</td>
                                <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                                <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-1 flex-wrap">
                                        {(r.status === "draft" || r.status === "rejected") && (hasRole(user, "admin") || r.evaluator === user?.email) && (
                                            <>
                                                <button onClick={() => openEdit(r)} className="p-2 text-ink/60 hover:text-klein" title="Edit" data-testid={`perf-edit-${r.id}`}>
                                                    <Edit3 className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => action(r, "submit")} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs uppercase tracking-[0.12em] border border-klein text-klein hover:bg-klein hover:text-white" title="Submit for approval" data-testid={`perf-submit-${r.id}`}>
                                                    <Send className="w-3.5 h-3.5" /> Submit
                                                </button>
                                            </>
                                        )}
                                        {r.status === "submitted" && (hasRole(user, "admin") || (hasRole(user, "department_lead") && (user?.department || "").toLowerCase() === (r.department || "").toLowerCase())) && (
                                            <>
                                                <button onClick={() => action(r, "dept-approve")} className="p-2 text-green-700 hover:bg-green-50" title="Dept approve" data-testid={`perf-dept-approve-${r.id}`}>
                                                    <Check className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => action(r, "reject")} className="p-2 text-red-700 hover:bg-red-50" title="Reject" data-testid={`perf-dept-reject-${r.id}`}>
                                                    <Ban className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                        {r.status === "dept_approved" && canHrApprove && (
                                            <>
                                                <button onClick={() => action(r, "hr-approve")} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs uppercase tracking-[0.12em] border border-klein text-klein hover:bg-klein hover:text-white" title="HR approve & finalize" data-testid={`perf-hr-approve-${r.id}`}>
                                                    <Award className="w-3.5 h-3.5" /> Finalize
                                                </button>
                                                <button onClick={() => action(r, "reject")} className="p-2 text-red-700 hover:bg-red-50" title="Reject" data-testid={`perf-hr-reject-${r.id}`}>
                                                    <Ban className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                        {hasRole(user, "admin") && (
                                            <button onClick={() => onDelete(r)} className="p-2 text-ink/60 hover:text-destructive hover:bg-destructive/5" title="Delete" data-testid={`perf-delete-${r.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && (
                <div className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" onClick={() => setOpen(false)} data-testid="perf-dialog">
                    <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-white border border-ink/10 w-full max-w-2xl">
                        <div className="flex items-center justify-between px-8 py-6 border-b border-ink/10">
                            <div>
                                <div className="overline">{editingId ? "Edit" : "New"} review</div>
                                <h3 className="mt-1 font-display font-bold text-xl">{editingId ? "Update scores" : "Score an employee"}</h3>
                            </div>
                            <button type="button" onClick={() => setOpen(false)} className="p-2 text-ink/60 hover:bg-ink/5">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-8 py-6 space-y-6">
                            {!editingId && (
                                <div>
                                    <label className="overline block mb-2">Employee *</label>
                                    {selectedEmp ? (
                                        <div className="flex items-center justify-between border border-klein bg-klein/5 px-3 py-2.5">
                                            <div className="text-sm">
                                                <div className="font-medium text-ink">{selectedEmp.first_name} {selectedEmp.last_name}</div>
                                                <div className="text-xs text-ink/60">{selectedEmp.employee_no || "—"}{selectedEmp.department ? ` · ${selectedEmp.department}` : ""}</div>
                                            </div>
                                            <button type="button" onClick={() => { setForm({ ...form, employee_id: "" }); setEmpSearch(""); }} className="p-1 text-ink/50 hover:text-ink">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="relative">
                                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40 pointer-events-none" />
                                                <input
                                                    type="search"
                                                    placeholder="Search name, staff no, department, division, phone…"
                                                    value={empSearch}
                                                    onChange={(e) => setEmpSearch(e.target.value)}
                                                    className="w-full border border-ink/15 py-2.5 pl-9 pr-3 text-sm focus:border-klein focus:outline-none"
                                                    data-testid="perf-emp-search"
                                                    autoFocus
                                                />
                                            </div>
                                            <div className="mt-2 max-h-44 overflow-y-auto border border-ink/10">
                                                {filteredEmployees.length === 0 ? (
                                                    <div className="px-3 py-4 text-xs text-ink/40">No matches.</div>
                                                ) : filteredEmployees.map((e) => (
                                                    <button
                                                        key={e.id}
                                                        type="button"
                                                        onClick={() => { setForm({ ...form, employee_id: e.id }); setEmpSearch(""); }}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-bone border-b border-ink/5 last:border-b-0"
                                                        data-testid={`perf-emp-option-${e.id}`}
                                                    >
                                                        <div className="font-medium text-ink">{e.first_name} {e.last_name}</div>
                                                        <div className="text-xs text-ink/50">{e.employee_no || "—"}{e.department ? ` · ${e.department}` : ""}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {!editingId && (
                                <div>
                                    <label className="overline block mb-2">Period *</label>
                                    <input
                                        type="text"
                                        value={form.period}
                                        onChange={(e) => setForm({ ...form, period: e.target.value })}
                                        placeholder="e.g. 2026-H1, 2026-Q2, or 2026"
                                        className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                        data-testid="perf-period-input"
                                        required
                                    />
                                </div>
                            )}

                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <label className="overline">Scores · 0 to {maxScore}</label>
                                    <div className="text-xs">
                                        Live: <span className="font-display font-bold text-klein tabular-nums">{previewTotals.normalized}</span>
                                        <span className="text-ink/40">/100</span>
                                        <span className={`ml-2 font-medium ${ratingColor(previewTotals.rating)}`}>{previewTotals.rating}</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {criteria.map((c) => (
                                        <div key={c.key} className="grid grid-cols-12 gap-3 items-center" data-testid={`perf-criterion-${c.key}`}>
                                            <div className="col-span-7">
                                                <div className="text-sm font-medium text-ink">{c.label}</div>
                                                <div className="text-[11px] text-ink/40 uppercase tracking-[0.15em]">Weight: {c.weight}%</div>
                                            </div>
                                            <div className="col-span-3 flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max={maxScore}
                                                    step="0.5"
                                                    value={form.scores?.[c.key] ?? 0}
                                                    onChange={(e) => setScore(c.key, e.target.value)}
                                                    className="w-full border border-ink/15 px-3 py-2 text-sm focus:border-klein focus:outline-none"
                                                    data-testid={`perf-score-${c.key}`}
                                                />
                                                <span className="text-xs text-ink/40 shrink-0">/{maxScore}</span>
                                            </div>
                                            <div className="col-span-2 h-1.5 bg-ink/5">
                                                <div className="h-full bg-klein" style={{ width: `${(Number(form.scores?.[c.key] || 0) / maxScore) * 100}%` }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="overline block mb-2">Manager comments</label>
                                <textarea
                                    rows="3"
                                    value={form.manager_comments}
                                    onChange={(e) => setForm({ ...form, manager_comments: e.target.value })}
                                    className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                                    placeholder="Strengths, areas to improve, agreed development plan…"
                                />
                            </div>
                        </div>
                        <div className="px-8 py-5 border-t border-ink/10 flex items-center justify-end gap-3">
                            <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 border border-ink/20 text-sm hover:bg-ink/5">Cancel</button>
                            <button type="submit" disabled={creating} className="inline-flex items-center gap-2 px-5 py-2.5 bg-klein text-white text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="perf-submit-button">
                                <Award className="w-4 h-4" /> {creating ? "Saving…" : (editingId ? "Update" : "Save draft")}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {cycleOpen && (
                <PerformanceCycleDialog
                    open={cycleOpen}
                    onClose={() => setCycleOpen(false)}
                    onLaunched={(data) => {
                        setPeriodFilter(data?.period || "");
                        setStatusFilter("draft");
                        load();
                    }}
                />
            )}

            {commentDialog && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="perf-comment-dialog">
                    <div className="bg-white max-w-lg w-full">
                        <div className="flex items-center justify-between p-5 border-b border-ink/10">
                            <div>
                                <div className="overline">{commentDialog.kind === "dept-approve" ? "Department Lead" : "HR finalize"}</div>
                                <h2 className="font-display font-bold text-xl mt-1">
                                    {commentDialog.kind === "dept-approve" ? "Approve & comment" : "Finalize review"}
                                </h2>
                            </div>
                            <button onClick={() => setCommentDialog(null)} className="p-2 hover:bg-ink/5" data-testid="perf-comment-dialog-close">
                                ✕
                            </button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="text-sm text-ink/70">
                                <strong>{commentDialog.review.employee_name}</strong> · {commentDialog.review.period}
                                {commentDialog.review.normalized_score != null && (
                                    <span className="ml-2 text-ink/50">· score {commentDialog.review.normalized_score}/100</span>
                                )}
                            </div>
                            <div>
                                <label className="overline">
                                    {commentDialog.kind === "dept-approve" ? "Your feedback (required) *" : "HR note (optional)"}
                                </label>
                                <textarea
                                    rows={4}
                                    value={commentDialog.note}
                                    onChange={(e) => setCommentDialog({ ...commentDialog, note: e.target.value })}
                                    placeholder={commentDialog.kind === "dept-approve"
                                        ? "Share specific feedback — what they did well, where to grow. Visible to the employee."
                                        : "Anything to record alongside the final approval."}
                                    className="mt-2 w-full border border-ink/20 px-3 py-2.5 text-sm focus:border-klein focus:outline-none"
                                    data-testid="perf-comment-input"
                                />
                                {commentDialog.kind === "dept-approve" && (
                                    <p className="mt-1 text-xs text-ink/50">This note is required and will be shown to the employee on their portal.</p>
                                )}
                            </div>
                        </div>
                        <div className="p-5 border-t border-ink/10 flex items-center justify-end gap-2">
                            <button onClick={() => setCommentDialog(null)} className="px-4 py-2 text-sm text-ink/60 hover:text-ink" data-testid="perf-comment-cancel">Cancel</button>
                            <button onClick={submitCommentDialog} className="inline-flex items-center gap-2 bg-klein text-white px-4 py-2 text-sm hover:bg-klein/90" data-testid="perf-comment-confirm">
                                {commentDialog.kind === "dept-approve" ? "Approve" : "Finalize"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
